import { IconButton, Panel, prose } from '@codecorp/components'
import { ReactNode, Key, useState, useId } from 'react'
import { uniqBy } from 'ramda'

export interface BarcodeCreatorDescription {
    title: string
    description: ReactNode
    icon: ReactNode
    component: ReactNode
    key: Key
}

export interface BarcodeTabsProps {
    barcodeCreators: { [title: string]: BarcodeCreatorDescription[] }
}

export const BarcodeTabs = ({ barcodeCreators }: BarcodeTabsProps) => {
    const [selectedTab, setSelectedTab] = useState<Key>()
    const containerId = useId()

    const tabEntries = Object.entries(barcodeCreators)
    const allTabs = tabEntries.flatMap(([_, v]) => v)
    if (uniqBy((x) => x.key, allTabs).length !== allTabs.length)
        throw new Error('keys must all be unique')

    const { component, description, icon, title } =
        allTabs.find((c) => c.key === selectedTab) ?? {}

    return (
        <div className="flex flex-col items-center justify-start pt-5">
            <Panel
                role="tablist"
                className="flex w-full flex-col pt-14 sm:w-4/6 md:w-5/6"
            >
                {tabEntries.map(([categoryTitle, categoryEntries]) => (
                    <div
                        className="flex flex-wrap place-content-evenly items-center "
                        key={categoryTitle}
                    >
                        {categoryEntries.map(({ icon, title, key }) => {
                            const id = `container-${containerId}-tab-${key}`

                            return (
                                <div
                                    key={key}
                                    aria-selected={selectedTab === key}
                                    onClick={() => setSelectedTab(key)}
                                    className="flex flex-col items-center p-4 text-xl opacity-60 hover:opacity-95 active:translate-y-2 active:transform aria-selected:opacity-100"
                                >
                                    <IconButton
                                        icon={icon}
                                        id={id}
                                        onPress={() => setSelectedTab(key)}
                                        isDisabled={selectedTab === key}
                                        role="tab"
                                        className=" w-14"
                                    />
                                    <label
                                        htmlFor={id}
                                        className="p-6"
                                    >
                                        {title}
                                    </label>
                                </div>
                            )
                        })}
                    </div>
                ))}
            </Panel>
            <div
                role="tabpanel"
                className="mt-10 flex flex-col items-center gap-8 md:flex-row-reverse md:items-start"
            >
                <div className="ml-10 mr-10 w-full shrink md:w-1/3 lg:w-2/5">
                    <div className="ml-auto mr-auto w-1/4 sm:w-1/4 md:ml-[30%] md:w-2/6">
                        {icon}
                    </div>
                    <h2 className=" ml-5 mr-5 mt-3 text-center text-lg font-medium md:text-left md:text-xl lg:text-2xl">
                        {title}
                    </h2>
                    <p
                        className={`ml-5 mr-5 text-center md:text-left ${prose}`}
                    >
                        {description}
                    </p>
                </div>
                <div className="w-full md:w-2/3 lg:w-2/5">{component}</div>
            </div>
        </div>
    )
}
